import { useSignOut } from "@gadgetinc/react";
import {
  AppType,
  Provider as GadgetProvider,
} from "@gadgetinc/react-shopify-app-bridge";
import { ConfigProvider } from "antd";
import posthog from "posthog-js";
import { Suspense, useContext, useEffect, useState } from "react";
import { Routes as DomRoutes, Route, useLocation } from "react-router-dom";
import { api } from "./api";
import {
  ForgotPasswordPage,
  InvitationPage,
  LayoutSidebar,
  NewShopifyInstallPage,
  SignInPage,
  SignUpPage,
} from "./externalComponents";
import { cleanInvitationUrl } from "./externalComponents/invitation-page";
import { WhatsappTwoFactorAuthentication } from "./externalComponents/WhatsappTwoFactorAuthentication";
import ExternalRoutes from "./ExternalRoutes";
import "./index.css";
import { CustomerContext, CustomerProvider } from "./providers";

const App = () => {
  const location = useLocation();
  const selectedMenu = location.pathname.split("/")[1];

  useEffect(() => {
    document.title = `Simio • ${selectedMenu
      .charAt(0)
      .toUpperCase()}${selectedMenu.slice(1)}`;
  }, [location.pathname]);

  return (
    <GadgetProvider
      type={AppType.Standalone}
      shopifyApiKey={window.gadgetConfig.apiKeys.shopify}
      api={api}
    >
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#22184B",
            borderRadius: 4,
          },
        }}
      >
        <CustomerProvider>
          <DomRoutes>
            {/* Default to authenticated app */}
            <Route path="/*" element={<AuthenticatedApp />} />{" "}
          </DomRoutes>
        </CustomerProvider>
      </ConfigProvider>
    </GadgetProvider>
  );
};

function AuthenticatedApp() {
  const { user, customer } = useContext(CustomerContext);

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const pages = import.meta.globEager(
    "./externalPages/**/!(*.test.[jt]sx)*.([jt]sx)",
  );

  const urlParams = new URLSearchParams(window.location.search);
  const invitationCode = urlParams.get("invitation_code");
  const companyName = urlParams.get("company_name");
  const isNewShopifyInstall = urlParams.get("new_shopify_install") === "true";

  // If 2FA is activated, disconnect user after 24 hours
  const signOut = useSignOut({ redirectToPath: "/dashboard" });
  const signOutToAzureLogin = useSignOut({
    redirectToPath: "/azure/login?forceLogin=true",
  });
  const yesterday = new Date();
  yesterday.setHours(yesterday.getHours() - 24);
  const now = new Date();
  useEffect(() => {
    if (
      user &&
      customer?.is2faActivated &&
      new Date(user.lastSignedIn) < yesterday &&
      !user?.isBlocked &&
      !customer?.azureTenantId
    ) {
      try {
        signOut();
        posthog.reset();
      } catch (error) {
        console.warn("Error during sign out:", error);
      }
    }
  }, [now, user, customer, yesterday]);

  // Azure AD session expiry check - 12 hours for production
  useEffect(() => {
    if (
      user &&
      customer?.azureTenantId &&
      user.lastSignedIn &&
      !user?.isBlocked
    ) {
      const lastSignIn = new Date(user.lastSignedIn);
      const twelveHoursAgo = new Date();
      twelveHoursAgo.setHours(twelveHoursAgo.getHours() - 12);

      if (lastSignIn < twelveHoursAgo) {
        console.log("Azure AD session expired after 12 hours, signing out");
        try {
          signOutToAzureLogin();
          posthog.reset();
        } catch (error) {
          console.warn("Error during Azure sign out:", error);
        }
      }
    }
  }, [user, customer, signOutToAzureLogin]);

  useEffect(() => {
    if (user?.isBlocked) {
      try {
        signOut();
        posthog.reset();
      } catch (error) {
        console.warn("Error during blocked user sign out:", error);
      }
    }
  }, [user]);

  useEffect(() => {
    try {
      const accessToken = urlParams.get("accessToken");
      const destinationPath = urlParams.get("destinationPath");
      if (accessToken) {
        api
          .signInWithAccessToken({ accessToken, destinationPath })
          .then((result) => {
            if (result.success) {
              window.location.href = result.destinationPath || "/dashboard";
            } else {
              console.error("Error signing in with access token", result);
            }
          });
      }
    } catch (error) {
      console.error("Error signing in with access token", error);
    }
  }, []);

  const handleSignIn = async (data) => {
    const { email, password } = data;
    setIsLoading(true);
    try {
      const result = await api.findUserIdByEmail({ email });

      if (!result || !result.id) {
        return false;
      }

      if (result.isBlocked) {
        return { isBlocked: result.isBlocked };
      }

      if (result.is2faActivated) {
        await api.user.checkConnection(result.id, {
          email,
          password,
        });

        setData({ email, password, ...result });
      } else {
        await api.user.emailSignIn(result.id, { email, password });
      }
      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (data, invitationCode) => {
    setIsLoading(true);
    try {
      let result =
        (await api.findUserIdByEmail({
          email: data.email,
        })) || {};

      if ((!result || !result.id) && !data.multiAccount) {
        const userRecord = await api.user.signUp({
          email: data.email,
          password: data.password,
          phone: data.phone,
          firstName: data.firstName,
          lastName: data.lastName,
          invitationCode: invitationCode?.trim(),
          customerName: data.companyName,
        });
        result.id = userRecord.id;
      } else if (result?.id && data.multiAccount) {
        const userRecord = await api.user.signUpWithExistingEmail(result.id, {
          email: data.email,
          password: data.password,
          invitationCode: invitationCode?.trim(),
        });
        cleanInvitationUrl();
        result.id = userRecord.id;
      }
      if (!result?.id) {
        throw new Error("User not found");
      }

      await api.user.emailSignIn(result.id, {
        email: data.email,
        password: data.password,
      });
    } catch (error) {
      console.error("Error during sign up:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.email && customer && customer.name) {
      posthog.identify(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        customerName: customer.name,
        isSimioEmployee:
          user.email.includes("@getsimio") || user.email.includes("@alcmeon"),
      });
    }
  }, [user, customer]);

  return (
    <Suspense fallback={<></>}>
      <WhatsappTwoFactorAuthentication data={data} setData={setData} />
      {user?.customer && (
        <LayoutSidebar onboardingProgress={customer.onboardingProgress}>
          <ExternalRoutes pages={pages} />
        </LayoutSidebar>
      )}
      {!user && !showForgotPassword && invitationCode && (
        <InvitationPage
          setShowSignUp={setShowSignUp}
          handleSignUp={handleSignUp}
          isLoading={isLoading}
          invitationCode={invitationCode}
          companyName={companyName}
        />
      )}
      {!user && !showForgotPassword && !invitationCode && showSignUp && (
        <SignUpPage
          setShowSignUp={setShowSignUp}
          handleSignUp={handleSignUp}
          isLoading={isLoading}
        />
      )}
      {!user &&
        !showForgotPassword &&
        !invitationCode &&
        !showSignUp &&
        !isNewShopifyInstall && (
          <SignInPage
            setShowSignUp={setShowSignUp}
            handleSignIn={handleSignIn}
            isLoading={isLoading}
            setShowForgotPassword={setShowForgotPassword}
          />
        )}
      {!user &&
        !showForgotPassword &&
        !invitationCode &&
        !showSignUp &&
        isNewShopifyInstall && (
          <NewShopifyInstallPage setShowSignUp={setShowSignUp} />
        )}
      {!user && showForgotPassword && (
        <ForgotPasswordPage
          setShowSignUp={setShowSignUp}
          handleSignIn={handleSignIn}
          isLoading={isLoading}
        />
      )}
    </Suspense>
  );
}

export default App;
